
import { VoiceProfile, VoiceOrchestrationContext } from './types';

export class VoiceOrchestrationEngine {
  private voiceProfiles: Record<string, VoiceProfile> = {
    interviewer: {
      voiceId: "21m00Tcm4TlvDq8ikWAM", // Default to Rachel
      name: "Interviewer",
      gender: "female",
      toneAttributes: ["professional", "warm", "clear"],
      suitableFor: ["interviews", "screening", "introduction"],
      defaultParameters: {
        stability: 0.75,
        similarityBoost: 0.7,
        speakingRate: 0.95
      }
    },
    technicalInterviewer: {
      voiceId: "AZnzlk1XvdvUeBnXmlld", // ElevenLabs' 'Adam'
      name: "Technical Interviewer",
      gender: "male",
      toneAttributes: ["technical", "analytical", "precise"],
      suitableFor: ["technical_questions", "coding_challenges"],
      defaultParameters: {
        stability: 0.8,
        similarityBoost: 0.6,
        speakingRate: 0.9
      }
    },
    hrSpecialist: {
      voiceId: "EXAVITQu4vr4xnSDxMaL", // ElevenLabs' 'Sarah'
      name: "HR Specialist",
      gender: "female",
      toneAttributes: ["friendly", "empathetic", "professional"],
      suitableFor: ["hr_interview", "behavioral_questions", "introduction"],
      defaultParameters: {
        stability: 0.7,
        similarityBoost: 0.8,
        speakingRate: 1.0
      }
    },
    seniorExecutive: {
      voiceId: "pNInz6obpgDQGcFmaJgB", // ElevenLabs' 'Josh'
      name: "Senior Executive",
      gender: "male",
      toneAttributes: ["authoritative", "confident", "measured"],
      suitableFor: ["follow_up", "closing", "high_level_screening"],
      defaultParameters: {
        stability: 0.85,
        similarityBoost: 0.7,
        speakingRate: 0.9
      }
    }
  };

  constructor() {
    // Initialize engine
  }

  /**
   * Get the best voice ID for a given context
   */
  public getVoiceForContext(context: VoiceOrchestrationContext): string {
    const { scenario, phase, candidateData } = context;
    
    // Select voice based on scenario and phase
    if (scenario === 'technical_interview') {
      return this.voiceProfiles.technicalInterviewer.voiceId;
    } else if (scenario === 'hr_interview') {
      return this.voiceProfiles.hrSpecialist.voiceId;
    } else if (scenario === 'follow_up') {
      return this.voiceProfiles.seniorExecutive.voiceId;
    } else if (phase === 'closing') {
      return this.voiceProfiles.seniorExecutive.voiceId;
    } else {
      // Default interviewer voice
      return this.voiceProfiles.interviewer.voiceId;
    }
  }

  /**
   * Enhance text with speech markers
   */
  public enhanceText(
    text: string,
    options?: {
      isQuestion?: boolean;
      emphasis?: string[];
      technicalTerms?: string[];
      sentiment?: 'positive' | 'neutral' | 'negative' | 'enthusiastic';
    }
  ): string {
    if (!options) return text;
    
    let enhancedText = text;
    
    // Emphasize technical terms
    if (options.technicalTerms && options.technicalTerms.length > 0) {
      options.technicalTerms.forEach(term => {
        const regex = new RegExp(`\\b${term}\\b`, 'gi');
        enhancedText = enhancedText.replace(regex, ` <emphasis level="moderate">${term}</emphasis> `);
      });
    }
    
    // Add emphasis markers
    if (options.emphasis && options.emphasis.length > 0) {
      options.emphasis.forEach(term => {
        const regex = new RegExp(`\\b${term}\\b`, 'gi');
        enhancedText = enhancedText.replace(regex, ` <emphasis level="strong">${term}</emphasis> `);
      });
    }
    
    // Add question inflection if needed
    if (options.isQuestion && !enhancedText.endsWith('?')) {
      enhancedText = enhancedText + '?';
    }
    
    // Add sentiment markers (for future voice AI that can interpret them)
    if (options.sentiment) {
      switch (options.sentiment) {
        case 'positive':
          enhancedText = `<voice emotion="positive">${enhancedText}</voice>`;
          break;
        case 'negative':
          enhancedText = `<voice emotion="concerned">${enhancedText}</voice>`;
          break;
        case 'enthusiastic':
          enhancedText = `<voice emotion="excited">${enhancedText}</voice>`;
          break;
        // neutral doesn't need a marker
      }
    }
    
    return enhancedText;
  }

  /**
   * Get parameters for voice synthesis based on context
   */
  public getVoiceParameters(context: VoiceOrchestrationContext): {
    stability?: number;
    similarityBoost?: number;
    style?: number;
    speakingRate?: number;
  } {
    const { scenario, phase } = context;
    
    // Base parameters on the selected voice profile
    let voiceProfile: VoiceProfile;
    
    if (scenario === 'technical_interview') {
      voiceProfile = this.voiceProfiles.technicalInterviewer;
    } else if (scenario === 'hr_interview') {
      voiceProfile = this.voiceProfiles.hrSpecialist;
    } else if (scenario === 'follow_up') {
      voiceProfile = this.voiceProfiles.seniorExecutive;
    } else if (phase === 'closing') {
      voiceProfile = this.voiceProfiles.seniorExecutive;
    } else {
      voiceProfile = this.voiceProfiles.interviewer;
    }
    
    // Return the default parameters for this voice
    return { ...voiceProfile.defaultParameters };
  }
}
